import { useEffect, useState } from "react";
import { Check, X, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

export const R_ORDER = ["delivered", "rejected", "on_time", "avg_duration", "late", "hours"];
const LABEL = { delivered: "المسلّمة", rejected: "المرفوضة", on_time: "في الوقت", avg_duration: "مدة التوصيل", late: "المتأخرة", hours: "ساعات الاتصال" };
const LOWER = new Set(["rejected", "avg_duration", "late"]);
const num = (v, d = 0) => Number(v).toLocaleString("en-US", { maximumFractionDigits: d });
const fmtR = (k, v) => (v == null ? "—" : k === "on_time" ? `${num(v * 100, 2)}%` : k === "avg_duration" ? `${num(v, 1)} د` : k === "hours" ? `${num(v, 1)} س` : num(v));
const toIn = (k, v) => (v == null ? "" : k === "on_time" ? String(+(v * 100).toFixed(2)) : String(v));
const fromIn = (k, v) => (k === "on_time" ? Number(v) / 100 : Number(v));

export const RTBadge = ({ kt, id }) => {
  if (!kt?.total) return <span className="text-[11px] text-slate-300">—</span>;
  const all = kt.met === kt.total;
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold ${all ? "bg-emerald-100 text-emerald-700" : "bg-rose-50 text-rose-600"}`} data-testid={`rider-targets-${id}`}>{kt.met}/{kt.total}</span>;
};

export const RTBars = ({ kt }) => {
  if (!kt?.total) return null;
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid="rider-target-bars">
      <p className="mb-2 font-heading text-sm font-bold text-slate-700">أهداف الشهر <span className="text-xs font-normal text-slate-400">· حقق {kt.met} من {kt.total}</span></p>
      <div className="grid gap-2 sm:grid-cols-2">{R_ORDER.filter((k) => kt.items[k]).map((k) => {
        const p = kt.items[k];
        return (
          <div key={k} data-testid={`rider-tbar-${k}`}>
            <div className="flex justify-between text-xs"><span className="text-slate-500">{LABEL[k]}</span>
              <span className={`flex items-center gap-0.5 font-semibold ${p.met ? "text-emerald-600" : "text-rose-500"}`}>{p.met ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}<span dir="ltr">{fmtR(k, kt.focus?.[k])} / {LOWER.has(k) ? "≤" : "≥"} {fmtR(k, p.target)}</span></span></div>
            <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${p.met ? "bg-emerald-500" : "bg-rose-400"}`} style={{ width: `${Math.min(100, p.pct * 100)}%` }} /></div>
          </div>
        );
      })}</div>
    </div>
  );
};

const Row = ({ label, sub, vals, onChange, onDel, tid, general }) => (
  <tr className={`border-t border-slate-100 ${general ? "bg-amber-50/60" : ""}`}>
    <td className="whitespace-nowrap px-2 py-1.5"><div className="font-semibold">{label}</div>{sub && <div className="text-[10px] text-slate-400">{sub}</div>}</td>
    {R_ORDER.map((k) => (
      <td key={k} className="px-1 py-1.5"><input type="number" min={0} step="any" dir="ltr" value={vals[k] ?? ""} placeholder={general ? "—" : "يرث"}
        onChange={(e) => onChange({ ...vals, [k]: e.target.value })} className="h-8 w-full rounded-md border border-slate-200 px-2 text-xs" data-testid={`rt-${tid}-${k}`} /></td>
    ))}
    <td className="px-1">{onDel && <button onClick={onDel} className="text-slate-300 hover:text-rose-500" data-testid={`rt-${tid}-del`}><Trash2 className="h-3.5 w-3.5" /></button>}</td>
  </tr>
);

export const RiderTargetsDialog = ({ open, onClose, platform, month, riders, onSaved }) => {
  const [rows, setRows] = useState(null);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.keetaRiderTargets(platform, month).then((d) => {
      const get = (scope, key) => d.targets.find((t) => t.scope === scope && t.key === key)?.targets || {};
      const conv = (t) => Object.fromEntries(R_ORDER.map((k) => [k, toIn(k, t[k])]));
      setRows([
        { scope: "all", key: "*", label: "الهدف العام", vals: conv(get("all", "*")) },
        ...d.supervisors.map((s) => ({ scope: "sup", key: s, label: `فريق ${s}`, vals: conv(get("sup", s)) })),
        ...d.targets.filter((t) => t.scope === "rider").map((t) => ({ scope: "rider", key: t.key, label: t.label, vals: conv(t.targets) })),
      ]);
    }).catch(() => toast.error("تعذّر التحميل"));
  }, [open, platform, month]);
  const set = (i, vals) => setRows(rows.map((r, j) => (j === i ? { ...r, vals } : r)));
  const addRider = () => {
    const r = riders.find((x) => x.rider_id === pick);
    if (!r || rows.some((x) => x.scope === "rider" && x.key === pick)) return;
    setRows([...rows, { scope: "rider", key: r.rider_id, label: r.rider_name || r.rider_id, vals: {} }]); setPick("");
  };
  const save = async () => {
    setBusy(true);
    try {
      for (const r of rows) {
        const targets = Object.fromEntries(R_ORDER.filter((k) => r.vals[k] !== "" && r.vals[k] != null).map((k) => [k, fromIn(k, r.vals[k])]));
        await api.keetaRiderTargetSave(platform, { month, scope: r.scope, key: r.key, targets });
      }
      toast.success("تم حفظ أهداف المناديب"); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  const del = async (i) => {
    const r = rows[i];
    await api.keetaRiderTargetSave(platform, { month, scope: r.scope, key: r.key, targets: {} }).catch(() => {});
    setRows(rows.filter((_, j) => j !== i));
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" dir="rtl" data-testid="rider-targets-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">أهداف المناديب · {month}</DialogTitle>
          <DialogDescription className="text-right">الأولوية: هدف المندوب ثم هدف فريق مشرفه ثم الهدف العام، لكل خانة لحالها. الأهداف تستمر للأشهر الجاية لين تغيّرها. «في الوقت» بالنسبة %، و«مدة التوصيل» بالدقائق، و«ساعات الاتصال» بالساعات.</DialogDescription></DialogHeader>
        {!rows ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-300" /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-xs">
                <thead><tr className="text-slate-500"><th className="px-2 py-2 text-right">المستوى</th>{R_ORDER.map((k) => <th key={k} className="px-1 py-2 text-right font-medium">{LABEL[k]} <span className="text-slate-400">{LOWER.has(k) ? "(حد أعلى)" : "(حد أدنى)"}</span></th>)}<th /></tr></thead>
                <tbody>{rows.map((r, i) => (
                  <Row key={`${r.scope}:${r.key}`} label={r.label} sub={r.scope === "rider" ? `#${r.key}` : null} vals={r.vals} onChange={(v) => set(i, v)}
                    general={r.scope === "all"} tid={r.scope === "all" ? "all" : `${r.scope}-${r.key}`} onDel={r.scope === "rider" ? () => del(i) : null} />
                ))}</tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} className="h-9 max-w-xs flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="rt-rider-pick">
                <option value="">اختر مندوب لهدف خاص…</option>
                {riders.filter((x) => !rows.some((r) => r.scope === "rider" && r.key === x.rider_id)).map((x) => <option key={x.rider_id} value={x.rider_id}>{x.rider_name || x.rider_id}{x.supervisor ? ` · ${x.supervisor}` : ""}</option>)}
              </select>
              <Button size="sm" variant="outline" className="gap-1" onClick={addRider} disabled={!pick} data-testid="rt-rider-add"><Plus className="h-4 w-4" /> إضافة</Button>
            </div>
          </>
        )}
        <div className="flex justify-end gap-2"><Button variant="outline" onClick={onClose}>إلغاء</Button><Button onClick={save} disabled={!rows || busy} data-testid="rt-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} حفظ الأهداف</Button></div>
      </DialogContent>
    </Dialog>
  );
};
